import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon,

  Globe,
  Key,
  Mail,
  Download,
  Upload,
  Shield,
  Server,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Info,
  Bot,
  Search,
  Undo2
} from 'lucide-react';
import { userService } from '../../services/database/userService';
import aiUsageService from '../../services/ai/AIUsageService';
import { AIUsageSettings, AIUserLimitInfo } from '../../types/ai';
import { normalizeVatliUrl } from '../../utils/urlNormalizer';
import type { User } from '../../types/auth';

interface SystemSettings {
  // WordPress API Settings
  wordpressApiUrl: string;
  wordpressApiKey: string;

  // Google OAuth Settings
  googleClientId: string;
  googleRedirectUri: string;

  // System Settings
  siteName: string;
  siteDescription: string;
  maintenanceMode: boolean;

  // Email Settings
  emailFrom: string;
  emailFromName: string;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;

  // Database Settings
  autoBackup: boolean;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
  maxBackups: number;
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'api' | 'database' | 'ai'>('api');
  const [settings, setSettings] = useState<SystemSettings>({
    // WordPress API
    wordpressApiUrl: import.meta.env.VITE_WORDPRESS_API_URL || '',
    wordpressApiKey: '',

    // Google OAuth
    googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
    googleRedirectUri: import.meta.env.VITE_GOOGLE_REDIRECT_URI || window.location.origin,

    // General
    siteName: 'Vatli365 - Hệ thống học tập',
    siteDescription: 'Nền tảng học vật lý trực tuyến',
    maintenanceMode: false,

    // Email
    emailFrom: 'noreply@vatli365.vn',
    emailFromName: 'Vatli365 System',
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',

    // Database
    autoBackup: true,
    backupFrequency: 'daily',
    maxBackups: 7,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [apiStatus, setApiStatus] = useState<'checking' | 'connected' | 'disconnected' | 'error'>('checking');
  const [testingConnection, setTestingConnection] = useState(false);

  const [aiSettings, setAiSettings] = useState<AIUsageSettings | null>(null);
  const [aiSettingsLoading, setAiSettingsLoading] = useState(true);
  const [aiSettingsError, setAiSettingsError] = useState<string | null>(null);
  const [aiDailyLimitInput, setAiDailyLimitInput] = useState(5);
  const [isSavingAi, setIsSavingAi] = useState(false);
  const [aiSaveStatus, setAiSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [userLimitSearch, setUserLimitSearch] = useState('');
  const [userLookupLoading, setUserLookupLoading] = useState(false);
  const [userLookupError, setUserLookupError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userLimitInfo, setUserLimitInfo] = useState<AIUserLimitInfo | null>(null);
  const [userLimitInput, setUserLimitInput] = useState(5);
  const [userLimitNotes, setUserLimitNotes] = useState('');
  const [userLimitSaving, setUserLimitSaving] = useState(false);
  const [userLimitStatus, setUserLimitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('adminSettings');
    if (savedSettings) {
      setSettings({ ...settings, ...JSON.parse(savedSettings) });
    }

    // Check API status
    checkApiStatus();
    loadAiSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkApiStatus = async () => {
    try {
      setApiStatus('checking');
      // Simple ping by fetching 1 user
      await userService.getAllUsers({ limit: 1 });
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('disconnected');
    }
  };

  /* Restored Functions */

  const loadAiSettings = async () => {
    try {
      setAiSettingsLoading(true);
      const data = await aiUsageService.getSettings();
      setAiSettings(data);
      setAiDailyLimitInput(data.dailyLimit);
      setAiSettingsError(null);
    } catch (error: any) {
      setAiSettingsError(error?.message || 'Không thể tải cấu hình giới hạn AI.');
    } finally {
      setAiSettingsLoading(false);
    }
  };

  const handleAiLimitSave = async () => {
    try {
      setIsSavingAi(true);
      setAiSaveStatus('idle');
      const data = await aiUsageService.updateSettings(aiDailyLimitInput);
      setAiSettings(data);
      setAiDailyLimitInput(data.dailyLimit);
      setAiSaveStatus('success');
      setTimeout(() => setAiSaveStatus('idle'), 3000);
    } catch (error: any) {
      setAiSaveStatus('error');
      setAiSettingsError(error?.message || 'Không thể cập nhật giới hạn AI.');
    } finally {
      setIsSavingAi(false);
    }
  };

  const lookupUserLimit = async () => {
    const keyword = userLimitSearch.trim()
    if (!keyword) {
      setUserLookupError('Nhập email của học sinh cần tìm.')
      return
    }

    try {
      setUserLookupLoading(true)
      setUserLookupError(null)
      setSelectedUser(null)
      setUserLimitInfo(null)

      const user =
        (await userService.getUserByEmail(keyword)) ||
        (await userService.getUserById(keyword))

      if (!user) {
        setUserLookupError('Không tìm thấy học sinh với email hoặc ID này.')
        return
      }

      setSelectedUser(user)
      const info = await aiUsageService.getUserLimit(user.id)
      setUserLimitInfo(info)
      setUserLimitInput(info.limitType === 'custom' ? info.dailyLimit : info.globalLimit)
      setUserLimitNotes(info.notes || '')
    } catch (error: any) {
      setUserLookupError(error?.message || 'Không thể tải thông tin giới hạn của học sinh.')
    } finally {
      setUserLookupLoading(false)
    }
  }

  const handleSaveUserLimit = async () => {
    if (!selectedUser) return
    try {
      setUserLimitSaving(true)
      setUserLimitStatus('idle')
      const info = await aiUsageService.setUserLimit(
        selectedUser.id,
        userLimitInput,
        userLimitNotes || undefined
      )
      setUserLimitInfo(info)
      setUserLimitStatus('success')
      setTimeout(() => setUserLimitStatus('idle'), 2500)
    } catch (error: any) {
      setUserLimitStatus('error')
      setUserLookupError(error?.message || 'Không thể cập nhật giới hạn cá nhân.')
    } finally {
      setUserLimitSaving(false)
    }
  }

  const handleResetUserLimit = async () => {
    if (!selectedUser) return
    try {
      setUserLimitSaving(true)
      setUserLimitStatus('idle')
      const info = await aiUsageService.deleteUserLimit(selectedUser.id)
      setUserLimitInfo(info)
      setUserLimitInput(info.globalLimit)
      setUserLimitNotes('')
      setUserLimitStatus('success')
      setTimeout(() => setUserLimitStatus('idle'), 2500)
    } catch (error: any) {
      setUserLimitStatus('error')
      setUserLookupError(error?.message || 'Không thể khôi phục giới hạn mặc định.')
    } finally {
      setUserLimitSaving(false)
    }
  }

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('idle');

    try {
      // Save to localStorage
      localStorage.setItem('adminSettings', JSON.stringify(settings));

      // Show success message
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    try {
      const start = Date.now();
      await userService.getAllUsers({ limit: 1 });
      const duration = Date.now() - start;
      alert('✅ Kết nối thành công!\n\n' +
        `Response Time: ${duration}ms\n` +
        `Backend: ${import.meta.env.VITE_PYTHON_API_URL ? 'Python/FastAPI' : 'WordPress/SQLite'}`);
      setApiStatus('connected');
    } catch (error: any) {
      alert('❌ Lỗi kết nối:\n\n' + error.message);
      setApiStatus('error');
    } finally {
      setTestingConnection(false);
    }
  };

  const exportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vatli365-settings-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const importSettings = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target?.result as string);
        setSettings({ ...settings, ...imported });
        alert('✅ Import thành công!');
      } catch (error) {
        alert('❌ Lỗi import file. Vui lòng kiểm tra định dạng file.');
      }
    };
    reader.readAsText(file);
  };

  const tabs = [
    { id: 'api', name: 'Kết nối Backend', icon: Server },

    { id: 'ai', name: 'Giới hạn AI', icon: Bot },
  ];



  const renderApiSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-medium mb-1">Kết nối Backend API</p>
          <p>Cấu hình kết nối đến server (Python FastAPI hoặc WordPress) để quản lý dữ liệu.</p>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            WordPress API URL
          </label>
          <div className="flex items-center space-x-2">
            <div className={`flex items-center space-x-1 text-xs px-2 py-1 rounded ${apiStatus === 'connected' ? 'bg-green-100 text-green-700' :
              apiStatus === 'checking' ? 'bg-yellow-100 text-yellow-700' :
                apiStatus === 'error' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
              }`}>
              {apiStatus === 'connected' && <CheckCircle className="w-3 h-3" />}
              {apiStatus === 'error' && <AlertCircle className="w-3 h-3" />}
              {apiStatus === 'checking' && <RefreshCw className="w-3 h-3 animate-spin" />}
              <span>{
                apiStatus === 'connected' ? 'Đã kết nối' :
                  apiStatus === 'checking' ? 'Đang kiểm tra...' :
                    apiStatus === 'error' ? 'Lỗi kết nối' :
                      'Chưa kết nối'
              }</span>
            </div>
          </div>
        </div>
        <input
          type="text"
          value={settings.wordpressApiUrl}
          onChange={(e) => setSettings({ ...settings, wordpressApiUrl: e.target.value })}
          placeholder="https://vatli365.vn/wp-json/vatli365/v1"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500">
          URL endpoint của Backend API (ví dụ: /wp-json/vatli365/v1 hoặc FastAPI URL)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          API Key (Optional)
        </label>
        <input
          type="password"
          value={settings.wordpressApiKey}
          onChange={(e) => setSettings({ ...settings, wordpressApiKey: e.target.value })}
          placeholder="API key nếu cần authentication"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <button
        onClick={testConnection}
        disabled={testingConnection}
        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {testingConnection ? (
          <RefreshCw className="w-4 h-4 animate-spin" />
        ) : (
          <Globe className="w-4 h-4" />
        )}
        <span>{testingConnection ? 'Đang kiểm tra...' : 'Kiểm tra kết nối'}</span>
      </button>
    </div>
  );





  const renderDatabaseSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-medium mb-1">Sao lưu Database</p>
          <p>Tự động sao lưu dữ liệu để đảm bảo an toàn.</p>
        </div>
      </div>

      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <p className="font-medium text-gray-900">Tự động sao lưu</p>
          <p className="text-sm text-gray-600">Bật sao lưu định kỳ tự động</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={settings.autoBackup}
            onChange={(e) => setSettings({ ...settings, autoBackup: e.target.checked })}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      {settings.autoBackup && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tần suất sao lưu
            </label>
            <select
              value={settings.backupFrequency}
              onChange={(e) => setSettings({ ...settings, backupFrequency: e.target.value as any })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="daily">Hàng ngày</option>
              <option value="weekly">Hàng tuần</option>
              <option value="monthly">Hàng tháng</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Số bản sao lưu tối đa
            </label>
            <input
              type="number"
              value={settings.maxBackups}
              onChange={(e) => setSettings({ ...settings, maxBackups: parseInt(e.target.value) })}
              min="1"
              max="30"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              Bản sao lưu cũ sẽ tự động xóa khi vượt quá số lượng này
            </p>
          </div>
        </>
      )}

      <div className="flex space-x-3">
        <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
          <Download className="w-4 h-4" />
          <span>Sao lưu ngay</span>
        </button>

        <label className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
          <Upload className="w-4 h-4" />
          <span>Khôi phục</span>
          <input type="file" accept=".json,.sql" className="hidden" />
        </label>
      </div>
    </div>
  );

  const renderAISettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-medium mb-1">Giới hạn lượt hỏi AI theo ngày</p>
          <p>Thiết lập số lượt hỏi/giải thích AI tối đa cho mỗi học sinh trong 24 giờ để tránh lạm dụng.</p>
        </div>
      </div>

      {aiSettingsError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>{aiSettingsError}</span>
        </div>
      )}

      {aiSettingsLoading ? (
        <div className="flex items-center space-x-2 text-gray-600">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Đang tải cấu hình AI...</span>
        </div>
      ) : (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Giới hạn lượt hỏi AI mỗi ngày
            </label>
            <input
              type="number"
              min={1}
              max={aiSettings?.maxLimit || 50}
              value={aiDailyLimitInput}
              onChange={(e) => {
                const raw = parseInt(e.target.value, 10)
                if (Number.isNaN(raw)) {
                  setAiDailyLimitInput(1)
                  return
                }
                const maxLimit = aiSettings?.maxLimit || 50
                const clamped = Math.min(Math.max(raw, 1), maxLimit)
                setAiDailyLimitInput(clamped)
              }}
              className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center font-semibold text-lg"
            />
            <p className="mt-2 text-sm text-gray-600">
              Hiện tại: <strong>{aiSettings?.dailyLimit}</strong> lượt/ngày · Tối đa {aiSettings?.maxLimit || 50} lượt/ngày
            </p>
          </div>

          <div className="flex items-center space-x-3">
            {aiSaveStatus === 'success' && (
              <span className="inline-flex items-center text-green-600 text-sm">
                <CheckCircle className="w-4 h-4 mr-1" />
                Đã cập nhật giới hạn
              </span>
            )}
            {aiSaveStatus === 'error' && (
              <span className="inline-flex items-center text-red-600 text-sm">
                <AlertCircle className="w-4 h-4 mr-1" />
                Cập nhật thất bại
              </span>
            )}

            <button
              onClick={handleAiLimitSave}
              disabled={isSavingAi}
              className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {isSavingAi ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Bot className="w-4 h-4" />
              )}
              <span>{isSavingAi ? 'Đang cập nhật...' : 'Cập nhật giới hạn'}</span>
            </button>
          </div>
        </>
      )}

      <div className="border-t pt-6">
        <h4 className="text-base font-semibold text-gray-900 mb-2">Giới hạn theo từng học sinh</h4>
        <p className="text-sm text-gray-600 mb-4">
          Cho phép đặt số lượt riêng cho các học sinh đặc biệt (ví dụ gói Pro). Nếu không đặt, hệ thống dùng giới hạn chung ở trên.
        </p>

        <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email hoặc User ID
            </label>
            <input
              type="text"
              value={userLimitSearch}
              onChange={(e) => setUserLimitSearch(e.target.value)}
              placeholder="ví dụ: student@example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={lookupUserLimit}
            disabled={userLookupLoading || !userLimitSearch.trim()}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 h-11"
          >
            {userLookupLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            <span>Tìm học sinh</span>
          </button>
        </div>

        {userLookupError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <span>{userLookupError}</span>
          </div>
        )}

        {selectedUser && userLimitInfo && (
          <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <p className="text-sm text-gray-500">Học sinh</p>
                <p className="font-semibold text-gray-900">
                  {selectedUser.name || selectedUser.email}
                </p>
                <p className="text-sm text-gray-600">{selectedUser.email}</p>
              </div>
              <div className="text-sm">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${userLimitInfo.limitType === 'custom'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-100 text-gray-700'
                    }`}
                >
                  {userLimitInfo.limitType === 'custom' ? 'Custom' : 'Mặc định'}
                </span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Giới hạn riêng (lượt/ngày)
                </label>
                <input
                  type="number"
                  min={1}
                  max={aiSettings?.maxLimit || 50}
                  value={userLimitInput}
                  onChange={(e) => setUserLimitInput(Math.max(1, Math.min(Number(e.target.value), aiSettings?.maxLimit || 50)))}
                  className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Giới hạn chung hiện tại: {userLimitInfo.globalLimit} lượt/ngày
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ghi chú
                </label>
                <input
                  type="text"
                  value={userLimitNotes}
                  onChange={(e) => setUserLimitNotes(e.target.value)}
                  placeholder="Ví dụ: Học sinh Pro, lớp nâng cao..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {userLimitStatus === 'success' && (
                <span className="inline-flex items-center text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Đã lưu thay đổi
                </span>
              )}
              {userLimitStatus === 'error' && (
                <span className="inline-flex items-center text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Lưu thất bại
                </span>
              )}

              <div className="flex items-center space-x-3 ml-auto">
                <button
                  onClick={handleResetUserLimit}
                  disabled={userLimitInfo.limitType !== 'custom' || userLimitSaving}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <Undo2 className="w-4 h-4" />
                  <span>Khôi phục mặc định</span>
                </button>

                <button
                  onClick={handleSaveUserLimit}
                  disabled={userLimitSaving}
                  className="flex items-center space-x-2 px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {userLimitSaving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Bot className="w-4 h-4" />
                  )}
                  <span>Lưu giới hạn riêng</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Cài đặt hệ thống</h1>
        <p className="text-gray-600 mt-2">Cấu hình chung cho toàn bộ hệ thống</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border p-4 sticky top-20">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${activeTab === tab.id
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            {activeTab === 'api' && renderApiSettings()}
            {activeTab === 'database' && renderDatabaseSettings()}
            {activeTab === 'ai' && renderAISettings()}

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t">
              <div className="flex items-center space-x-3">
                <button
                  onClick={exportSettings}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </button>

                <label className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <Upload className="w-4 h-4" />
                  <span>Import</span>
                  <input
                    type="file"
                    accept=".json"
                    onChange={importSettings}
                    className="hidden"
                  />
                </label>
              </div>

              <div className="flex items-center space-x-3">
                {saveStatus === 'success' && (
                  <div className="flex items-center space-x-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm">Đã lưu thành công</span>
                  </div>
                )}
                {saveStatus === 'error' && (
                  <div className="flex items-center space-x-2 text-red-600">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">Lưu thất bại</span>
                  </div>
                )}

                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center space-x-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {isSaving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{isSaving ? 'Đang lưu...' : 'Lưu cài đặt'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
